import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  signInWithEmailAndPassword,
  linkWithCredential,
  setPersistence,
} from "firebase/auth";
import { auth } from "../../../config/firebase";
import { IClientData } from "../../../models/ClientData";
import axios from "axios";
import { APIURL } from "../../../config/api";
import { sendLoginLink, claimAnonymousClient } from "../../../api/authApi";

// Used for accounts where the user never sets/sees their own password - both the old-lander
// auto-register flow and the anonymous-upgrade flow rely on this. The user always gets back in
// via a sign-in link, never by typing this password.
const generateTempPassword = () => Math.random().toString(36).slice(-8);

const getSafeRedirectPath = (fallback: string = "/dashboard") => {
  const urlParams = new URLSearchParams(window.location.search);
  const rawRedirect = urlParams.get("redirect");

  if (!rawRedirect) {
    return fallback;
  }

  if (/^https?:\/\//i.test(rawRedirect) || rawRedirect.startsWith("//")) {
    return fallback;
  }

  return rawRedirect.startsWith("/") ? rawRedirect : `/${rawRedirect}`;
};

export const AuthHelpers = {
  normalizeEmail: (email?: string | null) => (email ?? "").trim().toLowerCase(),

  checkAccountExists: async (email: string): Promise<boolean> => {
    const normalizedEmail = AuthHelpers.normalizeEmail(email);

    if (!normalizedEmail) {
      throw new Error("Email is required");
    }

    const response = await axios.post(`${APIURL}/auth/account-exists`, {
      email: normalizedEmail,
    });

    return Boolean(response.data?.exists);
  },

  requireAnonymousUser: () => {
    const currentUser = auth.currentUser;

    if (!currentUser?.isAnonymous) {
      throw new Error("An anonymous Firebase user is required before linking credentials");
    }

    return currentUser;
  },

  linkAnonymousWithEmailPassword: async (
    email: string,
    password: string
  ): Promise<string> => {
    const currentUser = AuthHelpers.requireAnonymousUser();

    await setPersistence(auth, browserLocalPersistence);
    const credential = EmailAuthProvider.credential(email, password);
    const linkedCredential = await linkWithCredential(currentUser, credential);

    return linkedCredential.user.uid;
  },

  /**
   * Upgrades an anonymous session to a full account, the same way the old-lander flow creates
   * accounts: a random password the user never sees or needs, with a sign-in link as the only
   * way back in. Unlike the old flow, this also carries the anonymous session's CRM client
   * record over to the new account, since (unlike old-lander users) this person may already
   * have quote/client data attached to their anonymous UID.
   *
   * Runs in the same tab/session as the anonymous user - there is no deferred, "finish this on
   * another device" version of this flow. Cross-device access is handled entirely by the
   * ordinary sign-in-link flow once the account exists (see sendSignInLink).
   *
   * @param email The email address to create the account with
   * @returns Promise resolving to the new account's Firebase UID
   */
  createFullAccountFromAnonymous: async (email: string): Promise<string> => {
    const anonymousUser = AuthHelpers.requireAnonymousUser();
    const normalizedEmail = AuthHelpers.normalizeEmail(email);

    // Must capture this before creating the new account below - doing so replaces
    // auth.currentUser, and there's no other way to get back to the anonymous session's token.
    const anonymousIdToken = await anonymousUser.getIdToken();

    await setPersistence(auth, browserLocalPersistence);

    const tempPassword = generateTempPassword();
    const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, tempPassword);
    const newUserIdToken = await userCredential.user.getIdToken();

    // Re-parent the anonymous session's CRM client record onto the new account. Best-effort:
    // the new account is already valid at this point, so a failure here shouldn't strand the
    // user - it just means their prior quote data won't be attached automatically.
    try {
      await claimAnonymousClient({ newUserIdToken, anonymousIdToken });
    } catch (error) {
      console.error("Failed to claim anonymous client data during account upgrade", error);
    }

    // Best-effort: email a standard sign-in link so there's a way back into this account later
    // without needing to know the randomly generated password.
    try {
      await AuthHelpers.sendSignInLink(normalizedEmail);
    } catch (error) {
      console.error("Failed to send sign-in link after anonymous account upgrade", error);
    }

    return userCredential.user.uid;
  },

  /**
   * Sends a sign-in link to the user's email
   * @param email User's email address
   * @param ExistsRedirectPath Optional path to redirect to after sign-in
   * @returns Promise that resolves when the link is sent
   */
  sendSignInLink: async (email: string): Promise<void> => {
    const redirectPath = getSafeRedirectPath("/dashboard");
    const normalizedEmail = AuthHelpers.normalizeEmail(email);

    const originUrl = `${window.location.origin}/auth/verify-email?redirect=${encodeURI(redirectPath)}`;

    window.localStorage.setItem("emailForSignIn", normalizedEmail);
    await sendLoginLink({ email: normalizedEmail, originUrl });
  },

  /**
   * Checks if a user exists and handles registration/sign-in accordingly
   * @param data Client data containing email
   * @returns Promise that resolves with the user ID and whether the account existed
   */
  handleQuickAuth: async (
    data: IClientData
  ): Promise<{ userId: string; accountExisted: boolean }> => {
    if (!data.email) {
      throw new Error("Email is required for registration");
    }

    try {
      const email = AuthHelpers.normalizeEmail(data.email);
      const accountExisted = await AuthHelpers.checkAccountExists(email);

      if (accountExisted) {
        return { userId: "pending", accountExisted: true };
      } else {
        // If account doesn't exist, create new account
        const tempPassword = generateTempPassword();
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          tempPassword
        );

        // Sign in the user immediately after account creation
        await signInWithEmailAndPassword(auth, email, tempPassword);

        return { userId: userCredential.user.uid, accountExisted: false };
      }
    } catch (error: any) {
      if (error.code === "auth/user-not-found") {
        const email = AuthHelpers.normalizeEmail(data.email);
        const tempPassword = generateTempPassword();
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          tempPassword
        );

        // Sign in the user immediately after account creation
        await signInWithEmailAndPassword(auth, email, tempPassword);

        return { userId: userCredential.user.uid, accountExisted: false };
      } else if (error.code === "auth/email-already-in-use") {
        return { userId: "pending", accountExisted: true };
      } else {
        throw error;
      }
    }
  },

  /**
   * Handles registration and navigation
   * @param data Client data containing email
   * @param insertClientData Function to insert client data into the backend
   * @param navigate Function to handle navigation
   * @param redirectPath Optional path to redirect to after successful registration
   * @param ExistsRedirectPath Optional path to redirect to after sign-in
   * @returns Promise that resolves with whether the account existed
   */
  handleRegistration: async (
    data: IClientData,
    insertClientData: (data?: IClientData) => Promise<number>,
    navigate: (path: string) => void,
    redirectPath: string = "/dashboard",
    ExistsRedirectPath: string = "/dashboard"
  ): Promise<boolean> => {
    if (!data.email) {
      throw new Error("Email is required");
    }

    try {
      //get redirect path from url
      const urlParams = new URLSearchParams(window.location.search);
      let redirect = urlParams.get("redirect");
      //below should check if there is a / or the encoded equivalent and add if it isn't there
      if (
        redirect !== null &&
        redirect !== "" &&
        redirect !== undefined &&
        redirect?.indexOf("/") === -1 &&
        redirect?.indexOf("%2F") === -1 &&
        redirectPath !== null &&
        redirectPath !== "" &&
        redirectPath !== undefined &&
        redirectPath?.indexOf("/") === -1 &&
        redirectPath?.indexOf("%2F") === -1
      ) {
        redirect = "/" + redirect;
      }

      if (redirect === null || redirect === "" || redirect === undefined) {
        redirect = redirectPath;
      }

      if (
        (redirect === null || redirect === "" || redirect === undefined) &&
        ExistsRedirectPath !== null &&
        ExistsRedirectPath !== "" &&
        ExistsRedirectPath !== undefined
      ) {
        redirect = ExistsRedirectPath;
      }

      // Handle authentication and get user ID
      const accountExisted = await AuthHelpers.handleQuickAuth(data);
      // Only insert client data if it's a new account
      if (!accountExisted.accountExisted) {
        // Insert client data only if account is new
        await insertClientData(data);
        navigate(redirect);
      }

      return accountExisted.accountExisted;
    } catch (error) {
      throw error;
    }
  },
};
